/**
 * Utility functions for extracting and processing hashtags from social media captions
 */

/**
 * Extracts hashtags from a text string
 * @param text - The text to extract hashtags from (e.g., post caption)
 * @returns Array of hashtags without the # symbol
 */
export const extractHashtags = (text: string): string[] => {
  if (!text) return [];
  
  // Regex to match hashtags (# followed by alphanumeric characters, underscores, or hyphens)
  const hashtagRegex = /#[\w\-]+/g;
  const matches = text.match(hashtagRegex);
  
  if (!matches) return [];
  
  // Remove the # symbol and convert to lowercase for consistency
  return matches.map(hashtag => hashtag.slice(1).toLowerCase());
};

/**
 * Gets the most commonly used hashtags from a collection of posts
 * @param posts - Array of social media posts
 * @returns Array of hashtags sorted by frequency (most common first)
 */
export const getTopHashtags = (posts: Array<{ hashtags?: string[] }>): Array<{ hashtag: string; count: number }> => {
  const hashtagCounts = new Map<string, number>();
  
  posts.forEach(post => {
    if (post.hashtags) {
      post.hashtags.forEach(hashtag => {
        hashtagCounts.set(hashtag, (hashtagCounts.get(hashtag) || 0) + 1);
      });
    }
  });
  
  return Array.from(hashtagCounts.entries())
    .map(([hashtag, count]) => ({ hashtag, count }))
    .sort((a, b) => b.count - a.count);
};

/**
 * Groups posts by hashtag for campaign analysis
 * @param posts - Array of social media posts
 * @returns Map of hashtag to posts containing that hashtag
 */
export const groupPostsByHashtag = (posts: Array<{ hashtags?: string[]; [key: string]: any }>): Map<string, any[]> => {
  const hashtagGroups = new Map<string, any[]>();
  
  posts.forEach(post => {
    if (post.hashtags) {
      post.hashtags.forEach(hashtag => {
        if (!hashtagGroups.has(hashtag)) {
          hashtagGroups.set(hashtag, []);
        }
        hashtagGroups.get(hashtag)!.push(post);
      });
    }
  });
  
  return hashtagGroups;
};

/**
 * Calculates campaign performance metrics for a specific hashtag
 * @param posts - Array of posts containing the hashtag
 * @returns Campaign performance metrics
 */
export const calculateHashtagCampaignMetrics = (posts: Array<{
  emv: number;
  likes: number;
  comments: number;
  shares: number;
  views: number;
  platform: string;
  timestamp: string;
}>) => {
  if (posts.length === 0) {
    return {
      totalPosts: 0,
      totalEMV: 0,
      averageEMV: 0,
      totalLikes: 0,
      totalComments: 0,
      totalShares: 0,
      totalViews: 0,
      platforms: [],
      dateRange: { start: '', end: '' },
      engagementRate: 0
    };
  }
  
  const totalEMV = posts.reduce((sum, post) => sum + post.emv, 0);
  const totalLikes = posts.reduce((sum, post) => sum + post.likes, 0);
  const totalComments = posts.reduce((sum, post) => sum + post.comments, 0);
  const totalShares = posts.reduce((sum, post) => sum + post.shares, 0);
  const totalViews = posts.reduce((sum, post) => sum + post.views, 0);
  
  const platforms = [...new Set(posts.map(post => post.platform))];
  const timestamps = posts.map(post => new Date(post.timestamp).getTime());
  const dateRange = {
    start: new Date(Math.min(...timestamps)).toISOString(),
    end: new Date(Math.max(...timestamps)).toISOString()
  };
  
  const totalEngagement = totalLikes + totalComments + totalShares;
  const engagementRate = totalViews > 0 ? (totalEngagement / totalViews) * 100 : 0;
  
  return {
    totalPosts: posts.length,
    totalEMV,
    averageEMV: totalEMV / posts.length,
    totalLikes,
    totalComments,
    totalShares,
    totalViews,
    platforms,
    dateRange,
    engagementRate
  };
};